import { QueryClient } from "@tanstack/react-query";
import camelcaseKeys from "camelcase-keys";
import decamelizeKeys from "decamelize-keys";

export const API_URL =
  import.meta.env.VITE_API_URL || "http://localhost:8000/api";

export const queryClient = new QueryClient();

let csrfToken = null;

export function setCsrfToken(token) {
  csrfToken = token;
}

export class ApiError extends Error {
  constructor(message, response, data = null) {
    super(message);
    this.name = "ApiError";
    this.response = response;
    this.data = data;
  }
}

export async function callApi(path, method, body, headers = {}) {
  if (method !== "GET") {
    if (csrfToken === null) {
      const { data } = await callApi("/csrf/", "GET");
      csrfToken = data.csrfToken;
    }
    headers["X-CSRFToken"] = csrfToken;
  }

  const options = {
    method: method,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  };
  if (body) {
    options.body = JSON.stringify(decamelizeKeys(body, { deep: true }));
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10_000);
  options.signal = controller.signal;

  let response;
  const url = `${API_URL}${path}`;

  try {
    response = await fetch(url, options);
    clearTimeout(timeoutId);
  } catch (error) {
    clearTimeout(timeoutId);
    throw new ApiError(error.message, null);
  }

  let data = null;
  try {
    data = await response.json();
    data = camelcaseKeys(data, { deep: true });
  } catch (error) {
    // Response body was empty or not valid JSON
    if (!response.ok) {
      throw new ApiError(
        `HTTP error ${response.status}: ${response.statusText}`,
        response
      );
    }
  }

  if (!response.ok) {
    throw new ApiError(
      `HTTP error ${response.status}: ${response.statusText}`,
      response,
      data
    );
  }

  return { response, data };
}

function getQueryString(params) {
  return Object.entries(params)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
}

export async function apiGet(path, params) {
  let endpoint = `/${path}/`;
  if (params) {
    const queryString = getQueryString(params);
    endpoint = `${endpoint}?${queryString}`;
  }
  return callApi(endpoint, "GET");
}

export async function apiPost(path, data) {
  return callApi(`/${path}/`, "POST", data);
}

export async function apiPatch(path, id, data) {
  return callApi(`/${path}/${id}/`, "PATCH", data);
}

export async function apiDelete(path, id) {
  return callApi(`/${path}/${id}/`, "DELETE");
}
